import { TFunction } from 'i18next';
import * as Yup from 'yup';

import { BotTypeEnum } from '@/utils/BotTypeEnum';

import AddDescription from './components/humanClone/AddDescription';
import AddVideo from './components/humanClone/AddVideo';
import Step1 from './components/marketplace/Step1';
import Step2 from './components/marketplace/Step2';
import Step3 from './components/marketplace/Step3';
import Step4 from './components/marketplace/Step4';
import AdditionalInfo from './components/quickBot/AdditionalInfo';
import ReactionSpeed from './components/quickBot/ReactionSpeed';
import SelectBotType from './components/selectType/SelectBotType';
import AutoUpdate from './components/siteBot/AutoUpdate';
import SelectLinks from './components/siteBot/SelectLinks';
import SetDomain from './components/siteBot/SetDomain';
import SetInformation from './components/siteBot/SetInformation';
import Templates from './components/Templates';

export type StepConfig = {
  key: string;
  schema: Yup.ObjectSchema<any> | ((formData: any, t: any) => Yup.ObjectSchema<any>);
  Component: React.ComponentType<any>;
  breadcrumbLabel?: string;
  props?: Record<string, any>;
};

const getAdditionalInfoSchema = (requiredFields: { name: string }[], t: any) => {
  const baseSchema: Record<string, Yup.AnySchema> = {
    additional_instructions: Yup.string().optional().label(t('Wizard/validationLabels/key2')),
    welcoming: Yup.string().optional().label(t('Wizard/validationLabels/key3')),
  };

  const fieldMap: Record<string, Yup.AnySchema> = {
    company_name: Yup.string().label(t('Wizard/validationLabels/key8')).required(),
    agent_name: Yup.string().max(30, 'Max length should be 30').label(t('Wizard/validationLabels/key9')).required(),
    domain: Yup.string().label(t('Wizard/validationLabels/key10')).required(),
    personality: Yup.string().label(t('Wizard/validationLabels/key11')).required(),
    legal_sector: Yup.string().label(t('Wizard/validationLabels/key12')).required(),
    business_sector: Yup.string().label(t('Wizard/validationLabels/key13')).required(),
    disclaimer: Yup.string().label(t('Wizard/validationLabels/key31')).required(),
    products_services: Yup.string().label(t('products_services')).required(),
    brand: Yup.string().label(t('brand')).required(),
    target_audience: Yup.string().label(t('target_audience')).required(),
    subject_expertise: Yup.string().label(t('subject_expertise')).required(),
    education_level: Yup.string().label(t('education_level')).required(),
    tech_specialization: Yup.string().label(t('tech_specialization')).required(),
    specialization: Yup.string().label(t('specialization')).required(),
    industry_focus: Yup.string().label(t('industry_focus')).required(),
    market_area: Yup.string().label(t('market_area')).required(),
    establishment_name: Yup.string().label(t('establishment_name')).required(),
    cuisine_type: Yup.string().label(t('cuisine_type')).required(),
    event_specialization: Yup.string().label(t('event_specialization')).required(),
    agency_name: Yup.string().label(t('agency_name')).required(),
  };

  // Collect instruction-level fields
  const instructionFields: Record<string, Yup.AnySchema> = {};
  for (const item of requiredFields) {
    const key = item.name;
    if (fieldMap[key]) {
      instructionFields[key] = fieldMap[key];
    }
  }

  // Add instructions as a nested object to the schema
  baseSchema.instructions = Yup.object().shape(instructionFields);

  return Yup.object().shape(baseSchema);
};

const getPackageDataSchema = (type: string, t: any) => {
  let dataShape: Record<string, Yup.AnySchema> = {
    description: Yup.string().required(t('marketplace/step3/description/validation/required')),
    image: Yup.string().url().required(),
    youtube_link: Yup.string().nullable().url(t('marketplace/step3/links/youtube/validation')).optional(),
    linkedin_link: Yup.string().nullable().url(t('marketplace/step3/links/linkedin/validation')).optional(),
  };

  let priceSchema: Yup.NumberSchema = Yup.number()
    .integer()
    .required(t('marketplace/step2/validation/priceRequired'))
    .min(0, t('marketplace/step2/validation/priceMin'))
    .typeError(t('marketplace/step2/validation/priceValid'));

  if (type === 'video') {
    priceSchema = priceSchema.max(499, t('marketplace/step2/validation/priceVideoMax'));

    dataShape = {
      ...dataShape,
      promotional_video: Yup.string().url().required('Promotional video is required'),
      delivery_delay: Yup.number()
        .integer()
        .required(t('marketplace/step2/validation/delivaryRequired'))
        .min(1, t('marketplace/step2/validation/delivaryMin'))
        .max(14, t('marketplace/step2/validation/delivaryMax'))
        .typeError(t('marketplace/step2/validation/delivaryValid')),
    };
  } else {
    priceSchema = priceSchema.max(199, t('marketplace/step2/validation/priceTextMax'));
  }

  return Yup.object().shape({
    price: priceSchema,
    data: Yup.object().shape(dataShape),
  });
};

export function getBotWizardConfig(botCode: string, t: TFunction, formData?: any, goToStep?: (step: number, section?: string) => void): StepConfig[] {
  const steps: StepConfig[] = [];

  const chooseTypeStep: StepConfig = {
    key: 'chooseType',
    schema: Yup.object({
      bot_code: Yup.string().required(t('Please select a bot type')),
      cloneExtra: Yup.object({
        template: Yup.string(),
        externalLinks: Yup.array(),
      }).optional(),
    }),
    Component: SelectBotType,
    breadcrumbLabel: t('Wizard/step1/key12'),
  };

  switch (botCode) {
    case BotTypeEnum.AI_READY_TO_USE:
    case BotTypeEnum.AI_WITH_KNOWLEDGE_BASE:
      steps.push(chooseTypeStep);

      steps.push({
        key: 'templates',
        schema: Yup.object({
          title: Yup.string().required(t('Wizard/step1/key13')).min(1).max(30, t('Wizard/validationLabels/key16')).label(t('Wizard/step1/key7').toString()),
          assistant: Yup.string().required(),
          required_instructions: Yup.array().of(Yup.mixed()),
        }),
        Component: Templates,
        props: { BotType: BotTypeEnum.AI_READY_TO_USE },
        breadcrumbLabel: t(botCode == BotTypeEnum.AI_READY_TO_USE ? 'Wizard/breadcrumbs/key1' : 'Wizard/breadcrumbs/key4'),
      });

      steps.push({
        key: 'additionalInfo',
        schema: (formData, t) => {
          const requiredFields = formData?.required_instructions || [];
          return getAdditionalInfoSchema(requiredFields, t);
        },
        Component: AdditionalInfo,
        breadcrumbLabel: t(botCode == BotTypeEnum.AI_READY_TO_USE ? 'Wizard/breadcrumbs/key2' : 'Wizard/breadcrumbs/key2'),
      });

      steps.push({
        key: 'speed',
        schema: Yup.object({
          configuration: Yup.string().required().label(t('Wizard/validationLabels/key5')),
        }),
        Component: ReactionSpeed,
        breadcrumbLabel: t(botCode == BotTypeEnum.AI_READY_TO_USE ? 'Wizard/breadcrumbs/key3' : 'Wizard/breadcrumbs/key6'),
      });
      break;

    case BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY:
      steps.push(chooseTypeStep);

      steps.push({
        key: 'setDomain',
        schema: Yup.object({
          plug_and_play: Yup.object({
            address: Yup.string()
              .matches(/^(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z()]{2,6}\b([-a-zA-Z0-9()!@:%_\\+.~#?&\\/\\/=]*)/i, {
                message: t('DashboardSettingWidgetDefaultDomain/er-01'),
              })
              .required(t('Wizard/PlugAndPlayInfos/key24')),
            isVerified: Yup.bool().oneOf([true], t('Wizard/PlugAndPlayInfos/key19')).required(t('Wizard/PlugAndPlayInfos/key19')).default(false),
            sitemap: Yup.array().of(Yup.string().url()).min(1),
          }),
        }),
        Component: SetDomain,
        breadcrumbLabel: t('Wizard/breadcrumbs/key10'),
      });

      steps.push({
        key: 'selectLinks',
        schema: Yup.object({
          plug_and_play: Yup.object({
            links: Yup.array().of(Yup.string().url().required()).min(1).required(),
          }),
        }),
        Component: SelectLinks,
        breadcrumbLabel: t('Wizard/breadcrumbs/key14'),
      });

      steps.push({
        key: 'setInfo',
        schema: Yup.object({
          title: Yup.string().required().min(1).max(30, t('Wizard/validationLabels/key16')).label(t('Wizard/step1/key7').toString()),
          additional_instructions: Yup.string().optional().label(t('Wizard/validationLabels/key2')),
        }),
        Component: SetInformation,
        breadcrumbLabel: t('Wizard/breadcrumbs/key15'),
      });

      steps.push({
        key: 'autoUpdate',
        schema: Yup.object({
          plug_and_play: Yup.object({
            auto_update: Yup.string().required().label(t('Wizard/validationLabels/key17')),
          }),
        }),
        Component: AutoUpdate,
        breadcrumbLabel: t('Wizard/breadcrumbs/key11'),
      });
      break;

    case BotTypeEnum.TEXT_SCENARIO:
    case BotTypeEnum.VIDEO_SCENARIO:
      steps.push(chooseTypeStep);

      steps.push({
        key: 'templates',
        schema: Yup.object({
          title: Yup.string().when('has_package', {
            is: false,
            then: schema =>
              schema.required(t('Wizard/step1/key13')).min(1).max(30, t('Wizard/validationLabels/key16')).label(t('Wizard/step1/key7').toString()),
            otherwise: schema => schema.optional().nullable(),
          }),
          template_id: Yup.string().required(t('General/key19')),
          package: Yup.mixed().optional().nullable(),
          has_package: Yup.boolean().default(false).required(),
        }),
        Component: Templates,
        props: {
          BotType: botCode === BotTypeEnum.TEXT_SCENARIO ? BotTypeEnum.TEXT_SCENARIO : BotTypeEnum.VIDEO_SCENARIO,
        },
        breadcrumbLabel: t(botCode == BotTypeEnum.TEXT_SCENARIO ? 'Wizard/breadcrumbs/key8' : 'Wizard/breadcrumbs/key9'),
      });
      // if (formData?.has_package) {
      //   steps.push({
      //     key: 'step4',
      //     schema: Yup.object({}),
      //     Component: Step4,
      //     breadcrumbLabel: t('marketplace/step4/review/title'),
      //   });
      // }

      break;

    case BotTypeEnum.HUMAN_CLONE:
      steps.push({
        key: 'chooseType',
        schema: Yup.object({
          title: Yup.string().required(t('Wizard/step1/key13')).min(1).max(30, t('Wizard/validationLabels/key16')).label(t('Wizard/step1/key7').toString()),
          bot_code: Yup.string().required(t('Please select a bot type')),
          cloneExtra: Yup.object({
            template: Yup.string().required('Please select a template'),
            externalLinks: Yup.array(),
          }).required(),
        }),
        Component: SelectBotType,
        breadcrumbLabel: t('Wizard/step1/key7'),
      });
      steps.push({
        key: 'additionalInfo',
        schema: (formData, t) => {
          const requiredFields = formData?.required_instructions || [];
          return getAdditionalInfoSchema(requiredFields, t);
        },
        Component: AdditionalInfo,
        breadcrumbLabel: t('Wizard/breadcrumbs/key2'),
      });

      if (formData?.cloneExtra?.template === 'clonePlusDescription') {
        steps.push({
          key: 'addDescription',
          schema: Yup.object({
            cloneExtra: Yup.object({
              data: Yup.object({
                name: Yup.string().required().label(t('Wizard/validationLabels/key18')),
                profession: Yup.string().label(t('Wizard/validationLabels/key19')),
                bio: Yup.string().required().label(t('Wizard/validationLabels/key20')),
              }),
              externalLinks: Yup.array(
                Yup.object({
                  logo: Yup.string().required().label(t('Wizard/validationLabels/key21')),
                  link: Yup.string().required().label(t('Wizard/validationLabels/key22')),
                })
              ),
            }).optional(),
          }),
          Component: AddDescription,
          breadcrumbLabel: t('Wizard/breadcrumbs/key12'),
        });
      } else if (formData?.cloneExtra?.template === 'clonePlusVideo') {
        steps.push({
          key: 'addVideo',
          schema: Yup.object({
            cloneExtra: Yup.object({
              data: Yup.object({
                botId: Yup.string().required(t('CloneExternalLinks/e-01')).label(t('Wizard/validationLabels/key23')),
              }),
              externalLinks: Yup.array(
                Yup.object({
                  logo: Yup.string().required().label(t('Wizard/validationLabels/key21')),
                  link: Yup.string().required().label(t('Wizard/validationLabels/key22')),
                }).required()
              ),
            }).optional(),
          }),
          Component: AddVideo,
          breadcrumbLabel: t('Wizard/breadcrumbs/key9'),
        });
      }

      steps.push({
        key: 'speed',
        schema: Yup.object({
          configuration: Yup.string().required().label(t('Wizard/validationLabels/key5')),
        }),
        Component: ReactionSpeed,
        breadcrumbLabel: t('Wizard/breadcrumbs/key3'),
      });
      break;

    case BotTypeEnum.CALLBOT:
      steps.push(chooseTypeStep);

      steps.push({
        key: 'templates',
        schema: Yup.object({
          title: Yup.string().required(t('Wizard/step1/key13')).min(1).max(30, t('Wizard/validationLabels/key16')).label(t('Wizard/step1/key7').toString()),
          assistant: Yup.string().required(),
          required_instructions: Yup.array().of(Yup.mixed()),
        }),
        Component: Templates,
        props: { BotType: BotTypeEnum.AI_READY_TO_USE },
        breadcrumbLabel: t('Wizard/breadcrumbs/key1'),
      });

      steps.push({
        key: 'additionalInfo',
        schema: (formData, t) => {
          const requiredFields = formData?.required_instructions || [];
          return getAdditionalInfoSchema(requiredFields, t);
        },
        Component: AdditionalInfo,
        breadcrumbLabel: t('Wizard/breadcrumbs/key2'),
      });
      break;

    case BotTypeEnum.MARKETPLACE:
      steps.push({
        key: 'step1',
        schema: Yup.object({
          type: Yup.string().required(),
          name: Yup.string().required(t('marketplace/step1/key1')),
          version: Yup.number().integer(t('marketplace/step1/key2')).required(t('marketplace/step1/key3')).typeError(t('marketplace/step1/key2')),
        }),
        Component: Step1,
        breadcrumbLabel: t('marketplace/step1/header/title'),
      });
      steps.push({
        key: 'Step2',
        schema: Yup.object({
          configuration: Yup.object({
            bots: Yup.array()
              .of(
                Yup.object().shape({
                  bot_id: Yup.string().required('ID is required'),
                  name: Yup.string().required('Name is required'),
                  type: Yup.string().required('Type is required'),
                })
              )
              .min(1)
              .test(
                'has-required-type',
                formData?.type === 'video' ? t('marketplace/step2/validation/bots/videoMinimum') : t('marketplace/step2/validation/bots/textMinimum'),
                function (bots) {
                  if (formData?.type === 'video') {
                    return bots?.some(bot => bot.type === 'VIDEO_SCENARIO');
                  }
                  if (formData?.type === 'text') {
                    return bots?.some(bot => bot.type === 'MCBOT_01');
                  }
                  return true;
                }
              )
              .required(),
          }),
        }),
        Component: Step2,
        breadcrumbLabel: t('marketplace/step2/header/title'),
      });
      steps.push({
        key: 'step3',
        schema: (formData, t) => {
          return getPackageDataSchema(formData.type, t);
        },
        Component: Step3,
        breadcrumbLabel: t('marketplace/step3/header/title'),
      });

      steps.push({
        key: 'step4',
        schema: Yup.object({}),
        Component: Step4,
        breadcrumbLabel: t('marketplace/step4/review/title'),
        props: {
          goToStep: goToStep,
        },
      });
      break;
    default:
      steps.push(chooseTypeStep);
  }

  return steps;
}
